import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { Link } from "react-router-dom";
import useCrmPermissions from "../hooks/useCrmPermissions";
import { useCompany } from "../../context/CompanyContext";
import OpportunityKanban from "../components/OpportunityKanban";
import {
  getOpportunities,
  getPipelineStages,
  moveOpportunityStage,
} from "../services/crmOpportunitiesService";
import "../styles/crm.css";

const OpportunitiesPage = () => {
  const { companyId } = useCompany();
  const [stages, setStages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState("");
  const [message, setMessage] = useState("");
  const { canCreateRecords } = useCrmPermissions();

  const load = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMessage("");

    try {
      const [stageRows, opportunityRows] = await Promise.all([
        getPipelineStages(companyId),
        getOpportunities(companyId),
      ]);

      setStages(stageRows);
      setOpportunities(opportunityRows);
    } catch (error) {
      setMessage(error.message || "Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMove = useCallback(
    async (opportunityId, toStageId) => {
      if (!companyId || !opportunityId || !toStageId) return;

      setMovingId(opportunityId);
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) throw userError;
        if (!user?.id) throw new Error("User not found");

        await moveOpportunityStage({
          opportunityId,
          companyId,
          toStageId,
          changedBy: user.id,
          note: "",
        });

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to move opportunity");
      } finally {
        setMovingId("");
      }
    },
    [companyId, load]
  );

  return (
    <div>
      <h2 className="crm-page-title">Opportunities</h2>

      {message ? <div className="crm-message">{message}</div> : null}
      {canCreateRecords ? (
        <div className="crm-inline-actions" style={{ marginBottom: "16px" }}>
          <Link className="crm-primary-btn" to="/crm/opportunities/new">
            Create Opportunity
          </Link>
        </div>
      ) : null}
      {loading ? (
        <div className="crm-message">Loading opportunities...</div>
      ) : (
        <OpportunityKanban
          stages={stages}
          opportunities={opportunities}
          onMove={handleMove}
          movingId={movingId}
        />
      )}
    </div>
  );
};

export default OpportunitiesPage;
